import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import buildUrl from 'build-url-ts';
import { useDispatch } from 'react-redux';
import _ from 'lodash';

import {
  useDocusignAuthenticate,
  useDocusignDeactivate,
  useDocusignIntegrationFetch,
} from 'src/query/settings/integrations/docusign';

import Slider from 'src/components/slider';
import BackButton from 'src/components/backButton';
import Button from 'src/components/button';
import { IntegrationTitle } from 'src/legacy/pages/settings/integrations/integrations/integrationsOnboarding/IntegrationTitle';
import { IntegrationSections } from 'src/legacy/pages/settings/integrations/integrations/integrationsOnboarding/IntegrationSections';
import SwitchButton from 'src/components/switchButton';
import { localizedStrings } from 'src/localization';
import { hideSideMenu } from 'src/redux/actions/hub';

import styles from './styles.module.scss';

const DocusignLogo = '/images/docusign.png';
const PuzzleImg = '/images/puzzle.svg';
const AuthenticationExpiredImg = '/images/authenticationExpired.svg';

const apiUrl = `${process.env.REACT_APP_API_URL}`;
const DocusignIntegration: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  const integration = localizedStrings.constants.integrationsOnboarding.integrations.docusign;
  const sections = integration.sections;

  const { data: integrationItem, isLoading: isIntegrationLoading, isFetched } = useDocusignIntegrationFetch();
  const { mutate: authenticate, isPending: isAuthenticating } = useDocusignAuthenticate();
  const { mutate: deactivate, isPending: isDeactivating, isSuccess: isDeactivated } = useDocusignDeactivate();

  const isIntegrationActive = integrationItem?.state?.code === 'ok';
  const isIntegrationExpired = integrationItem?.state?.code === 'auth_error';

  const [selectedTab, setSelectedTab] = useState<string>();
  const [isScrollDisabled, setIsScrollDisabled] = useState<boolean>(false);
  const [showIntegrationWalkthrough, setShowIntegrationWalkthrough] = useState<boolean>(false);
  const [showIntegrationStatus, setShowIntegrationStatus] = useState<boolean>(false);
  const [isAuthenticationExpired, setIsAuthenticationExpired] = useState<boolean>(false);
  const [showDeactivateConfirmation, setShowDeactivateConfirmation] = useState<boolean>(false);

  useEffect(() => {
    if (!isFetched) {
      return;
    }

    if (isIntegrationActive) {
      setShowIntegrationStatus(true);
      setShowIntegrationWalkthrough(false);
      setIsAuthenticationExpired(false);
    } else if (isIntegrationExpired) {
      setIsAuthenticationExpired(true);
      setShowIntegrationStatus(true);
      setShowIntegrationWalkthrough(false);
    } else {
      setShowIntegrationWalkthrough(true);
      setShowIntegrationStatus(false);
      setIsAuthenticationExpired(false);
    }
  }, [isFetched, isIntegrationActive, isIntegrationExpired]);

  useEffect(() => {
    if (isDeactivated) {
      navigate('/settings/integrations');
    }
  }, [isDeactivated, navigate]);

  const handleAuthenticateEvent = useCallback(
    (event: Event) => {
      const e = event as MessageEvent;
      if (e?.data?.type !== 'DOCUSIGN_AUTHORIZATION_SUCCESS') {
        return;
      }

      const code = e?.data?.payload?.code;
      if (code) {
        authenticate(code);
      }

      window.removeEventListener('message', handleAuthenticateEvent);
    },
    [authenticate]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('message', handleAuthenticateEvent);
    };
  }, [handleAuthenticateEvent]);

  const handleActivate = () => {
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : window.screen.width;
    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : window.screen.height;

    const w = 0.8 * width;
    const h = 0.8 * height;

    const left = width / 2 - w / 2 + dualScreenLeft;
    const top = height / 2 - h / 2 + dualScreenTop;

    const url = buildUrl(apiUrl, {
      path: '/api/2.0/integrations/docusign/authorize',
    });

    const newWindow = window.open(
      url,
      'Authorize DocuSign Account',
      `scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`
    );

    if (newWindow?.focus) {
      newWindow.focus();
    }

    window.addEventListener('message', handleAuthenticateEvent);
  };

  const handleClick = () => {
    if (isIntegrationActive) {
      setShowDeactivateConfirmation(true);
    } else {
      handleActivate();
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isScrollDisabled) {
      return;
    }

    const buffer = 25;
    let selectedTabIndex = 0;

    for (let i = 0; i < sections.length; i++) {
      const sectionId = sections[i].id;
      const element = document.getElementById(sectionId);

      if (element) {
        const { top } = element.getBoundingClientRect();
        if (e.currentTarget.scrollTop >= top - buffer) {
          selectedTabIndex = i;
        }
      }
    }

    setSelectedTab(sections[selectedTabIndex].id);
  };

  const disableScroll = (value: boolean) => {
    setIsScrollDisabled(value);
  };

  return (
    <Row className={styles.docusign}>
      <Col md={3} className={styles.sideBar}>
        <BackButton
          className={styles.back}
          title="Back"
          onClick={() => {
            dispatch(hideSideMenu(false));
            setTimeout(() => {
              navigate('/settings/integrations');
            }, 300);
          }}
        />

        <div className={styles.content}>
          <img src={DocusignLogo} alt="DocuSign" />

          <p>{integration.description}</p>

          <Button
            icon="paperflite-right-arrow"
            text={
              isIntegrationActive ? 'Deactivate' : isAuthenticationExpired ? 'Reauthenticate' : 'Activate'
            }
            variant="primary"
            disabled={isIntegrationLoading || isDeactivating || isAuthenticating}
            className={styles.activate}
            onClick={handleClick}
          />

          {(isIntegrationActive || isIntegrationExpired) && (
            <div className={styles.info}>
              <p>Learn more:</p>
              <p className={styles.toggle}>
                <p>Here is how the integration works </p>
                <SwitchButton
                  id="docusignIntegrationOnboarding"
                  label=""
                  className={styles.switch}
                  checked={showIntegrationWalkthrough}
                  handleCheck={() => {
                    setShowIntegrationWalkthrough(!showIntegrationWalkthrough);
                    setShowIntegrationStatus(!showIntegrationStatus);
                  }}
                />
              </p>
            </div>
          )}
        </div>
      </Col>

      <Col md={9} id="right" className={styles.right} onScroll={handleScroll}>
        {isIntegrationLoading && (
          <div className={styles.messageContainer}>
            <div>
              <p>Loading...</p>
            </div>
          </div>
        )}

        {isIntegrationActive && !isIntegrationLoading && showIntegrationStatus && (
          <div className={styles.messageContainer}>
            <div>
              <img src={PuzzleImg} alt="" />
              <p>DocuSign integration is currently active for {_.get(integrationItem, 'createdBy.email')}</p>
            </div>
          </div>
        )}

        {isAuthenticationExpired && !isIntegrationLoading && showIntegrationStatus && (
          <div className={styles.messageContainer}>
            <div>
              <img src={AuthenticationExpiredImg} alt="" />
              <p>Your authentication to DocuSign has expired and needs to be renewed.</p>
            </div>
          </div>
        )}

        {showIntegrationWalkthrough && (
          <div>
            <IntegrationTitle title={integration.title} />
            <IntegrationSections sections={sections} tab={selectedTab} disableScroll={disableScroll} />
          </div>
        )}
      </Col>

      <Slider.Dialog width="380px" show={showDeactivateConfirmation} onClose={() => setShowDeactivateConfirmation(false)}>
        <Slider.Header title="deactivating..." message="DocuSign" />

        <Slider.Body>
          <p>Do you wish to continue</p>
        </Slider.Body>

        <Slider.Actions>
          <Button
            variant="primary"
            className={styles.saveButton}
            icon="paperflite-right-arrow"
            text={isDeactivating ? 'Deactivating...' : 'Deactivate'}
            disabled={isDeactivating}
            onClick={() => deactivate()}
          />
          <Button
            variant="secondary"
            className={styles.cancelButton}
            onClick={() => setShowDeactivateConfirmation(false)}
            disabled={isDeactivating}
            text="Cancel"
          />
        </Slider.Actions>
      </Slider.Dialog>
    </Row>
  );
};

export default DocusignIntegration;
